import { Button, Card } from '@heroui/react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Heart, Settings } from 'lucide-react'
import { useState } from 'react'
import { useTranslations } from 'use-intl'
import { LocalizedLink } from '~/components/common/localized-link'
import { PublicLayout } from '~/components/layout/public-layout'
import { AUTH_ROUTES, getAuthRoute } from '~/lib/auth/routes'
import { createSessionQuery } from '~/lib/session-query'
import { PreferencesModal } from '~/features/dashboard/components/PreferencesModal'
import { BillingDashboardCard } from '~/features/billing/components/BillingDashboardCard'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(
      createSessionQuery(context.queryClient),
    )
    if (!session?.session) {
      throw redirect({
        to: getAuthRoute(AUTH_ROUTES.signIn) as any,
        search: ((prev: any) => ({ ...prev, redirectTo: location.href })) as any,
      })
    }
    return { session, user: session.user }
  },
  component: DashboardPage,
})

function DashboardPage() {
  const { user } = Route.useRouteContext()
  const t = useTranslations('dashboard')
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false)

  return (
    <PublicLayout>
      <div className="container mx-auto px-4 lg:px-8 py-12 max-w-6xl">
        <header className="mb-10">
          <h1 className="text-4xl font-black tracking-tight mb-2">
            {t('title')}
            {user?.name ? `, ${user.name}` : ''}!
          </h1>
          <p className="text-lg text-stone-500">
            {t('subtitle')}
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
          <Card 
            className="p-6 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-sm transition-all hover:shadow-md cursor-pointer"
          >
            <LocalizedLink to="/favorites" className="block w-full text-left">
              <Card.Header className="flex flex-col items-start gap-2 pb-2">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full flex items-center justify-center mb-2">
                  <Heart className="w-5 h-5 fill-current" />
                </div>
                <Card.Title className="text-xl font-bold">{t('collectionCardTitle')}</Card.Title>
              </Card.Header>
              <Card.Content>
                <p className="text-stone-500 text-sm mb-6">
                  {t('collectionCardDesc')}
                </p>
                <Button className="w-full font-medium" variant="danger-soft">
                  {t('goToFavorites')}
                </Button>
              </Card.Content>
            </LocalizedLink>
          </Card>

          <Card 
            className="p-6 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-sm transition-all hover:shadow-md cursor-pointer"
          >
            <div 
              role="button" 
              tabIndex={0}
              className="block w-full text-left outline-none"
              onClick={() => setIsPreferencesOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setIsPreferencesOpen(true)
                }
              }}
            >
              <Card.Header className="flex flex-col items-start gap-2 pb-2">
                <div className="w-10 h-10 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 rounded-full flex items-center justify-center mb-2">
                  <Settings className="w-5 h-5" />
                </div>
                <Card.Title className="text-xl font-bold">{t('preferencesCardTitle')}</Card.Title>
              </Card.Header>
              <Card.Content className="w-full text-left">
                <p className="text-stone-500 text-sm mb-6">
                  {t('preferencesCardDesc')}
                </p>
                <div className="w-full font-medium bg-stone-100 dark:bg-stone-800 py-2 text-center rounded-lg text-sm hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors">
                  {t('openPreferences')}
                </div>
              </Card.Content>
            </div>
          </Card>

          <BillingDashboardCard />
        </div>
      </div>
      <PreferencesModal isOpen={isPreferencesOpen} onOpenChange={setIsPreferencesOpen} user={user} />
    </PublicLayout>
  )
}
